import { useCallback, useEffect, useState } from "react";
import Cookies from "js-cookie";

import { API_HOLIDAY } from "../../config/Endpoints.js";
import { getApi } from "../utils/api.ts";
import {
  normalizeRegion,
} from "../components/features/holidays/constants.ts";
import type { Holiday } from "../components/features/holidays/types.ts";

type RawHoliday = Record<string, unknown>;

function extractHolidayRows(res: unknown): RawHoliday[] {
  if (!res || typeof res !== "object") return [];
  const d = res as Record<string, unknown>;
  const inner = d.data;
  if (inner && typeof inner === "object") {
    const inner2 = (inner as Record<string, unknown>).data;
    if (Array.isArray(inner2)) return inner2 as RawHoliday[];
    if (Array.isArray(inner)) return inner as RawHoliday[];
  }
  if (Array.isArray(d.data)) return d.data as RawHoliday[];
  return [];
}

function pickDescription(raw: RawHoliday): string {
  const keys = [
    "description",
    "holidayDescription",
    "remarks",
    "note",
    "details",
  ] as const;
  for (const k of keys) {
    const v = raw[k];
    if (typeof v === "string" && v.trim() !== "") return v.trim();
  }
  return "";
}

function mapRawToHoliday(raw: RawHoliday, index: number): Holiday | null {
  const idVal = raw.id ?? raw.holidayId ?? `${raw.date}-${raw.nameOfHoliday}`;
  const name =
    typeof raw.nameOfHoliday === "string"
      ? raw.nameOfHoliday
      : String(raw.nameOfHoliday ?? "Holiday");
  const year = Number(raw.year ?? new Date().getFullYear());
  const dateStr =
    typeof raw.date === "string"
      ? raw.date
      : raw.date != null
        ? String(raw.date)
        : "";
  const status =
    typeof raw.status === "string" ? raw.status : String(raw.status ?? "");

  const regionRaw =
    (typeof raw.shiftType === "string" && raw.shiftType) ||
    (typeof raw.country === "string" && raw.country) ||
    (typeof raw.region === "string" && raw.region) ||
    (typeof raw.officeLocation === "string" && raw.officeLocation) ||
    undefined;

  const region = normalizeRegion(regionRaw);
  if (region == null) return null;

  return {
    id: String(idVal ?? index),
    nameOfHoliday: name,
    year: Number.isFinite(year) ? year : new Date().getFullYear(),
    date: dateStr,
    status,
    region,
    description: pickDescription(raw),
  };
}

export function useHolidays(year: number) {
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const token = Cookies.get("accessToken");

  const fetchHolidays = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const url = `${API_HOLIDAY}?year=${year}&status=approved`;
      const res = await getApi<unknown>(url, token);
      const rows = extractHolidayRows(res.data)
        .map(mapRawToHoliday)
        .filter((h): h is Holiday => h != null);
      setHolidays(rows);
    } catch (e) {
      setHolidays([]);
      setError(e instanceof Error ? e.message : "Failed to load holidays");
    } finally {
      setLoading(false);
    }
  }, [year, token]);

  useEffect(() => {
    void fetchHolidays();
  }, [fetchHolidays]);

  return { holidays, loading, error, refetch: fetchHolidays };
}
